///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { LinkFeature } from '../../../entities/link/link-feature';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthPreloaderModalEvent } from '../../../../../shared/preloader-modal/preloader-modal-event';
import { GtthModifyLinkInfoCommand } from './modify-link-info-command';
import { NodeTypeEnum } from '../../../../../generated/shared-types.model';
import { GtthNodeFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-node-feature-dto.model';

export class GtthChangeSectorAngleCommand extends GtthAbstractCommand {
    private sectorId: number;
    private sectorInDevice: number;
    private angleDelta: number;
    private newAngle: number;
    private oldAngle: number;

    getCommandName(): string {
        return 'GtthChangeSectorAngleCommand';
    }

    create(sectorId: number, sectorInDevice: number, newAngle: number) {
        this.sectorId = sectorId;
        this.sectorInDevice = sectorInDevice;
        this.newAngle = newAngle;
        return this;
    }

    doCommand(): void {
        const deviceSectors = this.historyService.logicalTopology.getDeviceSectors(this.sectorId);
        if (!deviceSectors?.hasSectorInDevice(this.sectorInDevice)) {
            this.commandResolve(false);
            return;
        }

        this.oldAngle ??= deviceSectors.getDeviceSector(this.sectorInDevice).getLinks()[0].getSectorDirectionAngle();

        this.angleDelta = this.newAngle - this.oldAngle;
        const sectorLinks = [];
        deviceSectors.getActiveSectors().forEach((sector) => {
            sectorLinks.push(...this.changeLinksSectorAngle(sector.getLinks(), this.angleDelta));
        });

        const station = this.historyService.logicalTopology.getStation(deviceSectors.getSectorStationId());
        const bomDevice = this.historyService.bomReportService
            .getExtendedNodeDevices(-1, station.getName())
            .find((device) => device.sectorIndex === this.sectorId);

        if (bomDevice) {
            bomDevice.azimuth += this.angleDelta;
            bomDevice.deviceSectorsConfig.forEach((sector) => (sector.sectorInDeviceDirection += this.angleDelta));
        }

        const activity = this.createActivityDTO(false);
        activity.links = sectorLinks;

        if (station.getMhType() === NodeTypeEnum.BU_MUST || station.isPop()) {
            let deviceAzimuth = station.getBuDeviceAzimuth();
            if (deviceAzimuth === GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH) {
                deviceAzimuth = this.historyService.logicalTopology.autodesignP2mpPerLayer[station.getLayerIndex()].buDeviceAzimuth;
            }
            station.setBuDeviceAzimuth(deviceAzimuth + this.angleDelta);
            activity.nodes = [station.toGeoJson()];
        }
        this.sendActivity(activity);
    }

    undoCommand(): void {
        const deviceSectors = this.historyService.logicalTopology.getDeviceSectors(this.sectorId);
        if (!deviceSectors) {
            this.commandResolve(false);
            return;
        }
        const sectorLinks = [];
        deviceSectors.getActiveSectors().forEach((sector) => {
            sectorLinks.push(this.changeLinksSectorAngle(sector.getLinks(), -this.angleDelta));
        });

        const activity = this.createActivityDTO(false);
        activity.links = sectorLinks;
        const station = this.historyService.logicalTopology.getStation(deviceSectors.getSectorStationId());

        if (station.getMhType() === NodeTypeEnum.BU_MUST || station.isPop()) {
            station.setBuDeviceAzimuth(station.getBuDeviceAzimuth() - this.angleDelta);
            activity.nodes = [station.toGeoJson()];
        }
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        this.applyChanges(activityDto);
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        this.applyChanges(activityDto);
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    private changeLinksSectorAngle(links: LinkFeature[], delta: number): GtthLinkFeatureDtoModel[] {
        return links.map((link: LinkFeature) => {
            const geoJson = link.toGeoJson();
            geoJson.setSectorDirection(geoJson.getSectorDirection() + delta);
            return geoJson;
        });
    }

    private applyChanges(activityDto: ActivityDTO) {
        if (!activityDto.successful) {
            this.historyService.notificationService.showError(activityDto?.error?.message);

            console.log('Command failed:', activityDto.error);
        } else {
            const commandsArr = [];

            if (activityDto.links) {
                activityDto.links.forEach((linkJson: GtthLinkFeatureDtoModel) => {
                    const link = this.historyService.logicalTopology.getLink(linkJson.getId());
                    const modifyCommand = this.historyService.command(GtthModifyLinkInfoCommand).create(link.getId());

                    if (!link.isUserForced()) {
                        modifyCommand.setOldIsUserForced(link.isUserForced());
                        modifyCommand.setNewIsUserForced(linkJson.getIsUserForced());
                    }

                    modifyCommand.setOldSectorInfo(link.getSectorIndex(), link.getSectorApIndex(), link.getSectorDirectionAngle());
                    modifyCommand.setNewSectorInfo(linkJson.getSectorIdx(), linkJson.getSectorApIdx(), linkJson.getSectorDirection());
                    commandsArr.push(this.historyService.doCommandBypassHistory(modifyCommand, true));
                });

                Promise.all(commandsArr).then((_result) => {
                    this.historyService.bomReportService.saveBom();
                    if (this.commandResolve) {
                        this.commandResolve(activityDto.successful);
                    }
                });
            }

            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });
        }

        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }
}
